/**
 * What search engines need to know about the site: its public address, its languages and the agency's
 * details. Shared by the app (PageSEO, the language router) and by the build, which writes a ready HTML page
 * for every address (seo-build/prerender.ts). Keep it plain data: no React, no '@/…' imports, no
 * import.meta, because vite.config.ts loads it in Node.
 */

export const SITE_URL = 'https://aquamarinatours.art';
export const BRAND = 'AQUAMARINA TOURS';

/** Photo used when a page has none of its own (1200 × 630). */
export const DEFAULT_IMAGE =
  'https://static.readdy.ai/image/afb605347509a89ad173f501e678e265/31b216fbb61a1288cfc238e8821db2d0.jpeg?ogv=15jk2se';

export const AGENCY = {
  email: 'paraty.sirenatours@gmail.com',
  phone: '+55 11 95245-1307',
  phoneE164: '+5511952451307',
  whatsapp: 'https://wa.me/5511952451307',
  instagram: 'https://www.instagram.com/aquamarina_ll/',
  street: 'Av. Roberto Silveira, 578 - Centro',
  city: 'Paraty',
  region: 'RJ',
  postalCode: '23970-000',
  country: 'BR',
  latitude: -23.2178,
  longitude: -44.7139,
  logo: `${SITE_URL}/app-icon-512.png`,
};

export const AGENCY_ADDRESS = `${AGENCY.street}, ${AGENCY.city} - ${AGENCY.region}, ${AGENCY.postalCode}, Brasil`;

/** Who sells on the site, exactly as registered with the payment provider (dLocal Go); shown in the terms. */
export const SELLER = {
  name: 'Serafin Ponce Siles',
  document: '9384851',
};

/** Portuguese lives at the plain addresses; every other language has its own prefix (/es/escunas). */
export const SEO_LANGS = ['pt', 'es', 'en', 'fr'] as const;
export type SeoLang = (typeof SEO_LANGS)[number];

export const LANG_META: Record<SeoLang, { prefix: string; hreflang: string; htmlLang: string; ogLocale: string; name: string }> = {
  pt: { prefix: '', hreflang: 'pt-BR', htmlLang: 'pt-BR', ogLocale: 'pt_BR', name: 'Português' },
  es: { prefix: '/es', hreflang: 'es', htmlLang: 'es', ogLocale: 'es_LA', name: 'Español' },
  en: { prefix: '/en', hreflang: 'en', htmlLang: 'en', ogLocale: 'en_US', name: 'English' },
  fr: { prefix: '/fr', hreflang: 'fr', htmlLang: 'fr', ogLocale: 'fr_FR', name: 'Français' },
};

export const isSeoLang = (value: unknown): value is SeoLang =>
  typeof value === 'string' && (SEO_LANGS as readonly string[]).includes(value);

/** '/es/escunas' → { lang: 'es', path: '/escunas' }; '/es' → { lang: 'es', path: '/' }; '/escunas' → { lang: null, … }. */
export function splitLangPath(pathname: string): { lang: SeoLang | null; path: string } {
  const match = /^\/(es|en|fr)(?=\/|$)/i.exec(pathname || '/');
  if (!match) return { lang: null, path: pathname || '/' };
  return { lang: match[1].toLowerCase() as SeoLang, path: pathname.slice(match[0].length) || '/' };
}

/** The address of a page in a language: localizedPath('es', '/') → '/es', localizedPath('pt', '/escunas') → '/escunas'. */
export function localizedPath(lang: SeoLang, path: string): string {
  const clean = path && path !== '/' ? path : '/';
  const { prefix } = LANG_META[lang];
  if (!prefix) return clean;
  return clean === '/' ? prefix : prefix + clean;
}

export const absoluteUrl = (lang: SeoLang, path: string) => SITE_URL + localizedPath(lang, path);

/**
 * Netlify serves the ready pages in lower case and redirects any other spelling, so a tour address is always
 * written in lower case (the Aquamarina II slug starts with a capital letter in the catalog).
 */
export const tourPath = (slug: string) => `/passeio/${slug.toLowerCase()}`;

/**
 * Search engines, AI assistants and link previews. They read the Portuguese pages at the plain addresses,
 * whatever their own browser language is (Googlebot renders pages with an English browser).
 */
const CRAWLER_UA = /googlebot|google-inspectiontool|storebot-google|adsbot-google|mediapartners-google|apis-google|feedfetcher-google|google-extended|googleother|bingbot|bingpreview|msnbot|adidxbot|duckduckbot|duckassistbot|yandex|baiduspider|slurp|applebot|facebookexternalhit|facebookcatalog|facebot|meta-externalagent|twitterbot|linkedinbot|whatsapp|telegrambot|discordbot|slackbot|pinterest|redditbot|skypeuripreview|gptbot|chatgpt-user|oai-searchbot|claudebot|claude-user|claude-searchbot|anthropic-ai|perplexitybot|perplexity-user|ccbot|amazonbot|bytespider|petalbot|semrushbot|ahrefsbot|mj12bot|dotbot|seznambot|qwantbot|mojeekbot|ia_archiver|archive\.org_bot|chrome-lighthouse|headlesschrome|prerender/i;

export const isCrawler = (userAgent: string | undefined | null) => CRAWLER_UA.test(userAgent || '');
